import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

export type PostgridCountryFilter = 'US' | 'CA';

export interface PostgridAddressInput {
  line1?: string;
  line2?: string;
  city?: string;
  provinceOrState?: string;
  postalOrZip?: string;
  country?: string;
}

export interface PostgridSuggestion {
  label: string;
  line1: string;
  line2: string;
  city: string;
  provinceOrState: string;
  postalOrZip: string;
  country: string;
  postgridVerified: true;
  raw: unknown;
}

export interface PostgridGeocode {
  latitude: number;
  longitude: number;
}

export interface PostgridVerificationResult {
  line1: string;
  line2: string;
  city: string;
  provinceOrState: string;
  postalOrZip: string;
  country: string;
  countryName: string;
  geocode: PostgridGeocode | null;
  postgridVerified: true;
  raw: unknown;
}

interface PostgridCompletionsResponse {
  data?: Array<{
    address?: {
      address?: string;
      address2?: string;
      city?: string;
      prov?: string;
      pc?: string;
      country?: string;
    };
    preview?: {
      address?: string;
      city?: string;
      pc?: string;
      country?: string;
    };
  }>;
}

interface PostgridVerificationResponse {
  data?: {
    line1?: string;
    line2?: string;
    city?: string;
    provinceOrState?: string;
    postalOrZip?: string;
    country?: string;
    countryName?: string;
    geocodeResult?: {
      location?: {
        lat?: number;
        lng?: number;
      };
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class PostgridService {
  private proxyUrl = '/api/postgrid/postgrid.php';

  constructor(private http: HttpClient) {}

  verifyAddress(address: PostgridAddressInput): Observable<PostgridVerificationResult> {
    return this.http.post<PostgridVerificationResponse>(this.proxyUrl, {
      operation: 'verify',
      address
    }).pipe(
      map((response) => this.normalizeVerification(response))
    );
  }

  suggestAddress(query: string, country?: PostgridCountryFilter): Observable<PostgridSuggestion[]> {
    const body: { operation: string; partialStreet: string; countryFilter?: PostgridCountryFilter } = {
      operation: 'suggest',
      partialStreet: query
    };

    if (country) {
      body.countryFilter = country;
    }

    return this.http.post<PostgridCompletionsResponse>(this.proxyUrl, body).pipe(
      map((response) => this.normalizeSuggestions(response))
    );
  }

  private normalizeSuggestions(response: PostgridCompletionsResponse): PostgridSuggestion[] {
    return (response.data || [])
      .map((item) => {
        const address = item.address || {};
        const preview = item.preview || {};
        const line1 = this.clean(address.address || preview.address);
        const line2 = this.clean(address.address2);
        const city = this.clean(address.city || preview.city);
        const provinceOrState = this.clean(address.prov);
        const postalOrZip = this.clean(address.pc || preview.pc).replace(/\.\.\.$/, '');
        const country = this.clean(address.country || preview.country);
        const label = [line1, city, provinceOrState, postalOrZip, country].filter(Boolean).join(', ');

        return {
          label,
          line1,
          line2,
          city,
          provinceOrState,
          postalOrZip,
          country,
          postgridVerified: true as const,
          raw: item
        };
      })
      .filter((suggestion) => suggestion.label.length > 0);
  }

  private normalizeVerification(response: PostgridVerificationResponse): PostgridVerificationResult {
    const data = response.data || {};
    const location = data.geocodeResult?.location;
    const geocode = typeof location?.lat === 'number' && typeof location?.lng === 'number'
      ? { latitude: location.lat, longitude: location.lng }
      : null;

    return {
      line1: this.clean(data.line1),
      line2: this.clean(data.line2),
      city: this.clean(data.city),
      provinceOrState: this.clean(data.provinceOrState),
      postalOrZip: this.clean(data.postalOrZip),
      country: this.clean(data.country),
      countryName: this.clean(data.countryName || data.country),
      geocode,
      postgridVerified: true,
      raw: response
    };
  }

  private clean(value: unknown): string {
    return String(value ?? '').trim();
  }
}
